import React, { useState, useCallback } from 'react'
import { StepIndicator } from './StepIndicator'
import { Connector } from './Connector'
import { BankDetails, BankVerificationProps } from '../../interfaces/bank'
import { BankDetailsStep } from './BankDetailsStep'
import { VerificationStep } from './VerificationStep'
import { SuccessStep } from './SuccessStep'
import { FailedStep } from './FailedStep'

// ── Types ─────────────────────────────────────────────────────────────────────
type VerificationStep = 'bank-details' | 'verification' | 'confirmation'
type ConfirmationStatus = 'success' | 'failed' | null
type StepStatus = 'pending' | 'active' | 'done' | 'error'

// ── BankVerification (Main Export) ────────────────────────────────────────────
const BankVerification: React.FC<BankVerificationProps> = ({ onBack, onSuccess, onFailed }) => {
  const [step, setStep] = useState<VerificationStep>('bank-details')
  const [confirmationStatus, setConfirmationStatus] = useState<ConfirmationStatus>(null)
  const [bankDetails, setBankDetails] = useState<BankDetails | null>(null)

  const getStepStatus = (target: VerificationStep): StepStatus => {
    if (target === 'bank-details') return step === 'bank-details' ? 'active' : 'done'
    if (target === 'verification') {
      if (step === 'bank-details') return 'pending'
      if (step === 'verification') return 'active'
      return 'done'
    }
    if (step !== 'confirmation') return 'pending'
    if (confirmationStatus === 'failed') return 'error'
    if (confirmationStatus === 'success') return 'done'
    return 'active'
  }

  const handleVerify = (details: BankDetails) => {
    setBankDetails(details)
    setStep('verification')
  }

  const handleComplete = useCallback(
    (status: 'success' | 'failed') => {
      setConfirmationStatus(status)
      setStep('confirmation')
      if (status === 'success' && bankDetails) onSuccess(bankDetails)
      if (status === 'failed' && bankDetails) onFailed(bankDetails)
    },
    [bankDetails] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleEdit = () => {
    setStep('bank-details')
    setConfirmationStatus(null)
  }

  return (
    <div className="w-full">
      {/* Back */}
      {/* <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200 mb-8 group"
      >
        <span className="group-hover:-translate-x-0.5 transition-transform duration-200">
          <ArrowLeftIcon />
        </span>
        Back
      </button> */}

      {/* Stepper */}
      <div className="flex items-center mb-14">
        <StepIndicator label="Bank Details" status={getStepStatus('bank-details')} />
        <Connector active={step !== 'bank-details'} />
        <StepIndicator label="Verification" status={getStepStatus('verification')} />
        <Connector active={step === 'confirmation'} />
        <StepIndicator label="Confirmation" status={getStepStatus('confirmation')} />
      </div>

      {/* Step Content */}
      {step === 'bank-details' && <BankDetailsStep onVerify={handleVerify} />}
      {step === 'verification' && bankDetails && (
        <VerificationStep details={bankDetails} onComplete={handleComplete} />
      )}
      {step === 'confirmation' && bankDetails && confirmationStatus === 'success' && (
        <SuccessStep details={bankDetails} onEdit={handleEdit} />
      )}
      {step === 'confirmation' && bankDetails && confirmationStatus === 'failed' && (
        <FailedStep details={bankDetails} onEdit={handleEdit} />
      )}
    </div>
  )
}

export default BankVerification